/**
 * Configuration for XML components that can be parsed from LLM responses
 */
export interface XmlComponentConfig {
  type: 'wireframe' | 'shape' | 'device' | 'frame';
  wireframeName?: string;
  shapeName?: string;
  defaultProps?: Record<string, any>;
  placeholderOptions?: {
    defaultValue?: string;
    defaultIconName?: string;
    defaultIconPosition?: string;
    alwaysAddIcon?: boolean;
  };
  specialItemTags?: string[]; // Tags that this component can handle as special items
  processAttributes?: (baseObj: any) => any; // Custom attribute processor if needed
}

/**
 * Registry mapping XML tag names to their component configurations
 */
export const xmlComponentRegistry: Record<string, XmlComponentConfig> = {
  // Layout components
  device: {
    type: 'device',
    defaultProps: {
      orientation: 'portrait',
      layoutAlignHorizontal: 'center',
      layoutAlignVertical: 'top',
      deviceName: 'mobile',
      layoutEnabled: false,
      layoutDirection: 'vertical',
      layoutSizeVertical: 'user',
      layoutSizeHorizontal: 'user',
    },
  },

  vstack: {
    type: 'frame',
    defaultProps: {
      layoutSizeVertical: 'children',
      layoutSizeHorizontal: 'children',
      layoutAlignHorizontal: 'center',
      layoutAlignVertical: 'top',
      layoutDirection: 'vertical',
      layoutEnabled: false,
    },
  },

  hstack: {
    type: 'frame',
    defaultProps: {
      layoutSizeVertical: 'children',
      layoutSizeHorizontal: 'children',
      layoutAlignHorizontal: 'center',
      layoutAlignVertical: 'top',
      layoutDirection: 'horizontal',
      layoutEnabled: false,
    },
  },

  // Basic components
  rectangle: {
    type: 'shape',
    shapeName: 'rect',
  },

  text: {
    type: 'shape',
    shapeName: 'text',
  },

  // Form elements
  button: {
    type: 'wireframe',
    wireframeName: 'button',
  },

  input: {
    type: 'wireframe',
    wireframeName: 'inputField',
  },

  link: {
    type: 'wireframe',
    wireframeName: 'button',
    defaultProps: {
      style: 'link',
    },
  },

  // Selection components
  checkbox: {
    type: 'wireframe',
    wireframeName: 'checkbox',
  },

  radio: {
    type: 'wireframe',
    wireframeName: 'radioButton',
  },

  // Icon component
  icon: {
    type: 'shape',
    shapeName: 'icon',
    processAttributes: (baseObj: any) => {
      const { iconName, ...cleanBaseObj } = baseObj;

      return {
        ...cleanBaseObj,
        type: 'shape',
        shapeName: 'icon',
        iconName: `fas/${iconName ?? 'question'}`,
      };
    },
  },

  // Navigation components
  'nav-vertical': {
    type: 'wireframe',
    wireframeName: 'navMenuVertical',
    defaultProps: {
      itemsData: {},
    },
    specialItemTags: ['nav-item'],
  },

  'nav-horizontal': {
    type: 'wireframe',
    wireframeName: 'navMenuHorizontal',
    defaultProps: {
      itemsData: {},
    },
    specialItemTags: ['nav-item'],
  },

  breadcrumb: {
    type: 'wireframe',
    wireframeName: 'breadcrumb',
    defaultProps: {
      itemsData: {},
    },
    specialItemTags: ['breadcrumb-item'],
  },

  // Additional components
  pagination: {
    type: 'wireframe',
    wireframeName: 'pagination',
  },

  'search-bar': {
    type: 'wireframe',
    wireframeName: 'searchBar',
    defaultProps: {
      itemsData: {},
    },
    placeholderOptions: {
      defaultValue: 'Search...',
      defaultIconName: 'magnifying-glass',
      defaultIconPosition: 'left',
      alwaysAddIcon: true,
    },
  },

  'date-picker': {
    type: 'wireframe',
    wireframeName: 'datePicker',
    placeholderOptions: {
      defaultValue: '',
      defaultIconName: 'calendar-days',
      defaultIconPosition: 'right',
      alwaysAddIcon: true,
    },
  },

  dropdown: {
    type: 'wireframe',
    wireframeName: 'dropdown',
    defaultProps: {
      itemsData: {},
    },
    specialItemTags: ['dropdown-option'],
  },

  toggle: {
    type: 'wireframe',
    wireframeName: 'toggle',
  },

  slider: {
    type: 'wireframe',
    wireframeName: 'slider',
  },

  progress: {
    type: 'shape',
    shapeName: 'progressBar',
  },

  image: {
    type: 'wireframe',
    wireframeName: 'image',
  },

  video: {
    type: 'wireframe',
    wireframeName: 'video',
  },

  avatar: {
    type: 'wireframe',
    wireframeName: 'avatar',
    placeholderOptions: {
      defaultValue: '',
      defaultIconName: 'user',
      defaultIconPosition: 'left',
      alwaysAddIcon: true,
    },
  },

  pill: {
    type: 'wireframe',
    wireframeName: 'pill',
  },

  chart: {
    type: 'wireframe',
    wireframeName: 'barChart',
    defaultProps: {
      style: 'image',
    },
  },

  map: {
    type: 'wireframe',
    wireframeName: 'map',
    defaultProps: {
      style: 'image',
    },
  },

  'scroll-bar': {
    type: 'wireframe',
    wireframeName: 'scrollBar',
  },
};
